
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Layout from "./components/Layout";
import MinimalLayout from "./components/MinimalLayout";
import AdminLayout from "./components/admin/AdminLayout";
import ScrollToTop from "./components/ScrollToTop";
import GoogleAnalyticsTracker from "./components/GoogleAnalyticsTracker";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import GiftCard from "./pages/GiftCard";
import GiftCardSuccess from "./pages/GiftCardSuccess";
import GiftCardCancel from "./pages/GiftCardCancel";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";
import LegalNotice from "./pages/LegalNotice";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AppointmentAction from "./pages/AppointmentAction";
import CancelAppointment from "./pages/CancelAppointment";
import BookingTest from "./pages/BookingTest";

// Lazy load admin pages for better performance
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminClientDetail = lazy(() => import("./pages/admin/AdminClientDetail"));
const AdminGiftCards = lazy(() => import("./pages/admin/AdminGiftCards"));
const AdminInfo = lazy(() => import("./pages/admin/AdminInfo"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const AdminLoadingFallback = () => (
  <div className="pt-24 flex justify-center items-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bbz-green"></div>
    <span className="ml-3 text-lg">Chargement...</span>
  </div>
);

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <CookieConsentProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
        <GoogleAnalyticsTracker />
        <ScrollToTop />
        <Routes>
          {/* Admin login */}
          <Route 
            path="/admin/login" 
            element={
              <Suspense fallback={<AdminLoadingFallback />}>
                <AdminLogin />
              </Suspense>
            } 
          />

          {/* Admin routes with authentication protection */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route 
              path="dashboard" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              } 
            />
            <Route 
              path="services" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminServices />
                </Suspense>
              } 
            />
            <Route 
              path="calendar" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminCalendar />
                </Suspense>
              } 
            />
            <Route 
              path="clients" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminClients />
                </Suspense>
              } 
            />
            <Route 
              path="clients/:id" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminClientDetail />
                </Suspense>
              } 
            />
            <Route 
              path="gift-cards" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminGiftCards />
                </Suspense>
              } 
            />
            <Route 
              path="info" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminInfo />
                </Suspense>
              } 
            />
            <Route 
              path="statistics" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminStatistics />
                </Suspense>
              } 
            />
            <Route 
              path="messages" 
              element={
                <Suspense fallback={<AdminLoadingFallback />}>
                  <AdminMessages />
                </Suspense>
              } 
            />
          </Route>

          {/* Public routes with minimal layout (no AI assistant) */}
          <Route path="/" element={<MinimalLayout />}>
            <Route path="annulation-rendez-vous" element={<CancelAppointment />} />
          </Route>

          {/* Public routes with main layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="institut-beaute-bio-montpellier" element={<About />} />
            <Route path="soins-visage-massages-epilations-montpellier" element={<Services />} />
            <Route path="bon-cadeaux-estheticienne-bio-montpellier" element={<GiftCard />} />
            <Route path="bon-cadeau-confirmation" element={<GiftCardSuccess />} />
            <Route path="bon-cadeau-annulation" element={<GiftCardCancel />} />
            <Route path="contact-institut-montpellier" element={<Contact />} />
            <Route path="questions-frequentes-institut-beaute" element={<FAQ />} />
            <Route path="mentions-legales" element={<LegalNotice />} />
            <Route path="politique-confidentialite" element={<PrivacyPolicy />} />
            <Route path="gestion-rendez-vous" element={<AppointmentAction />} />
            <Route path="test-reservation" element={<BookingTest />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
        </TooltipProvider>
      </CookieConsentProvider>
    </QueryClientProvider>
  );
};

export default App;
